import * as React from 'react';
import { t } from '@reos/i18n';
import { Camera, Check, Plus, Trash2 } from 'lucide-react';
import type { DictationSuggestionDto, RoomFieldDto, RoomTemplateDto } from '../../lib/field-intake-api.js';
import type { Recorder } from '../../lib/field-intake-recorder.js';
import { roomScopeKey, suggestionForRoomField, type DictationStates } from '../../lib/intake-dictation.js';
import { DictationMic, DictationSuggestion } from './IntakeDictation.js';
import {
  addRoom,
  applyBulkPreset,
  copyFromPreviousRoom,
  mediaRoomTypeOf,
  planRoomsForDisposition,
  removeRoom,
  renameRoom,
  roomDone,
  roomFieldsByGroup,
  roomSummary,
  roomsForDisposition,
  setRoomField,
  type Room,
  type RoomValue,
} from '../../lib/intake-rooms.js';

/**
 * Krok NAB3 — ZALOZKA „MISTNOSTI" NABORU (zadani R.B. 14. 10. 2026).
 *
 * Makler nevypisuje mistnosti rucne: predgeneruji se z DISPOZICE z checklistu
 * (3+1 = obyvaci pokoj, 2x pokoj, kuchyn, koupelna s WC, predsin). U kazde pak
 * doplni plochu, podlahu, okna a stineni — a protoze to je v bytě vetsinou
 * vsude stejne, ma na to „stejne jako predchozi mistnost" a hromadne
 * predvolby („vsechna okna plast trojsklo, 2015").
 *
 * Mobil prvni: kazde tlacitko je cil klepnuti >= 44 px (`--rh-touch-h`),
 * mistnost je sbalitelna karta s poctem vyplnenych polí.
 *
 * ZAPIS JDE TOUTEZ CESTOU jako kazda odpoved checklistu — jeden fakt
 * `room_list` (fronta -> polozka relace -> potvrzeni -> `setFact`). Zadna
 * druha cesta zapisu tu nevznika.
 */

const tr = (key: Parameters<typeof t>[0], values?: Record<string, string | number>): string =>
  t(key, { locale: 'cs', ...(values !== undefined ? { values } : {}) });

const trRooms = (key: string, values?: Record<string, string | number>): string =>
  tr(`webAdmin.fieldIntake.roomsTab.${key}` as Parameters<typeof t>[0], values);

function parseFieldValue(field: RoomFieldDto, raw: string): RoomValue | undefined {
  const text = raw.trim();
  if (text === '') return null;
  if (field.control === 'number' || field.control === 'year') {
    const parsed = Number(text.replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (field.control === 'list') {
    return text
      .split(/[;,\n]/)
      .map((item) => item.trim())
      .filter((item) => item !== '')
      .slice(0, 20);
  }
  return text.slice(0, 300);
}

function valueLabel(field: RoomFieldDto, value: RoomValue | undefined): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return trRooms(value ? 'yes' : 'no');
  const option = field.options.find((candidate) => candidate.value === value);
  if (option !== undefined) return option.label;
  return field.unit === null ? String(value) : `${String(value)} ${field.unit}`;
}

function Field({
  field,
  roomId,
  value,
  suggestion,
  onSet,
  onAcceptSuggestion,
  onDismissSuggestion,
}: {
  readonly field: RoomFieldDto;
  readonly roomId: string;
  readonly value: RoomValue | undefined;
  /** Krok NAB4 — navrh z diktatu u tohoto pole mistnosti. */
  readonly suggestion: DictationSuggestionDto | null;
  readonly onSet: (value: RoomValue) => void;
  readonly onAcceptSuggestion: (suggestion: DictationSuggestionDto) => void;
  readonly onDismissSuggestion: (suggestion: DictationSuggestionDto) => void;
}): React.JSX.Element {
  const [raw, setRaw] = React.useState('');
  const testid = `nab3-field-${roomId}-${field.id}`;
  const answered = value !== undefined && value !== null && value !== '';

  return (
    <div
      className={`rh-fi-point rh-fi-cl__point${suggestion !== null ? ' rh-fi-point--suggested' : ''}`}
      data-testid={testid}
      data-answered={answered ? 'yes' : 'no'}
      data-suggested={suggestion !== null ? 'yes' : 'no'}
    >
      <div className="rh-fi-cl__q">
        <span className={`rh-fi-check${answered ? ' is-on' : ''}`} aria-hidden="true">
          {answered ? <Check size={18} strokeWidth={3} /> : null}
        </span>
        <span className="rh-v4-row__text">
          <span className="rh-v4-row__title">{field.question}</span>
        </span>
      </div>

      {answered ? (
        <div className="rh-fi-cl__answer" data-testid={`nab3-answer-${roomId}-${field.id}`}>
          {valueLabel(field, value)}
        </div>
      ) : null}

      {suggestion !== null ? (
        <DictationSuggestion
          suggestion={suggestion}
          testid={`nab4-room-${roomId}-${field.id}`}
          onAccept={() => onAcceptSuggestion(suggestion)}
          onDismiss={() => onDismissSuggestion(suggestion)}
        />
      ) : null}

      {field.control === 'select' ? (
        <div className="rh-fi-cl__chips">
          {field.options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip${value === option.value ? ' is-on' : ''}`}
              aria-pressed={value === option.value}
              onClick={() => onSet(option.value)}
              data-testid={`nab3-opt-${roomId}-${field.id}-${option.value}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}

      {field.control === 'yes_no_unknown' ? (
        <div className="rh-fi-cl__chips">
          {[true, false].map((option) => (
            <button
              key={String(option)}
              type="button"
              className={`rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip${value === option ? ' is-on' : ''}`}
              aria-pressed={value === option}
              onClick={() => onSet(option)}
              data-testid={`nab3-opt-${roomId}-${field.id}-${String(option)}`}
            >
              {trRooms(option ? 'yes' : 'no')}
            </button>
          ))}
          {/* „Nevím" neni „ne": hodnota se nezapise (pravidlo NAB2). */}
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip rh-fi-cl__chip--unknown"
            onClick={() => onSet(null)}
            data-testid={`nab3-unknown-${roomId}-${field.id}`}
          >
            {trRooms('unknown')}
          </button>
        </div>
      ) : null}

      {field.control === 'number' || field.control === 'year' || field.control === 'text' || field.control === 'list' ? (
        <div className="rh-fi-cl__input">
          <input
            className="rh-input rh-fi-input rh-fi-input--grow"
            value={raw}
            inputMode={field.control === 'number' || field.control === 'year' ? 'decimal' : 'text'}
            aria-label={field.question}
            placeholder={field.unit ?? ''}
            onChange={(event) => setRaw(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              const parsed = parseFieldValue(field, raw);
              if (parsed !== undefined) onSet(parsed);
              setRaw('');
            }}
            data-testid={`nab3-value-${roomId}-${field.id}`}
          />
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--chip"
            onClick={() => {
              const parsed = parseFieldValue(field, raw);
              if (parsed !== undefined) onSet(parsed);
              setRaw('');
            }}
            data-testid={`nab3-save-${roomId}-${field.id}`}
          >
            {trRooms('save')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function IntakeRooms({
  template,
  disposition,
  rooms,
  onRooms,
  onPhoto,
  photoCounts = {},
  recorder,
  dictations = {},
  onDictate,
  onAcceptSuggestion,
  onDismissSuggestion,
}: {
  readonly template: RoomTemplateDto | null;
  /** Dispozice z checklistu (fakt `disposition`); `null` = jeste nevybrana. */
  readonly disposition: string | null;
  readonly rooms: readonly Room[];
  readonly onRooms: (rooms: readonly Room[]) => void;
  /** Fotka u mistnosti — `roomType` je ciselnik `MEDIA_ROOM_TYPES`. */
  readonly onPhoto: (room: { readonly roomId: string; readonly roomType: string | null }, file: File) => void;
  readonly photoCounts?: Readonly<Record<string, number>>;
  /** Krok NAB4 — mikrofon u kazde mistnosti (diktat do 60 s). */
  readonly recorder?: Recorder | undefined;
  readonly dictations?: DictationStates | undefined;
  readonly onDictate?: ((scopeKey: string, blob: Blob, durationS: number) => void) | undefined;
  readonly onAcceptSuggestion?: ((suggestion: DictationSuggestionDto) => void) | undefined;
  readonly onDismissSuggestion?: ((suggestion: DictationSuggestionDto) => void) | undefined;
}): React.JSX.Element {
  const [addOpen, setAddOpen] = React.useState(false);

  if (template === null) {
    return (
      <section className="rh-v4-group rh-fi-card" data-testid="nab3-rooms-loading">
        <div className="rh-fi-card__sub">{trRooms('loading')}</div>
      </section>
    );
  }

  const groups = roomFieldsByGroup(template);
  const plan = planRoomsForDisposition(template, rooms, disposition);
  const knownDisposition = template.dispositions.some((entry) => entry.value === disposition);

  return (
    <div className="rh-fi-rooms" data-testid="nab3-rooms">
      <section className="rh-v4-group rh-fi-cl__head">
        <div className="rh-fi-cl__progress">
          <span data-testid="nab3-room-count">{trRooms('count', { count: rooms.length })}</span>
        </div>

        {disposition === null ? (
          <p className="rh-fi-note rh-fi-note--warn" data-testid="nab3-need-disposition">
            {trRooms('needDisposition')}
          </p>
        ) : null}

        {disposition !== null && !knownDisposition ? (
          <p className="rh-fi-note rh-fi-note--warn" data-testid="nab3-unknown-disposition">
            {trRooms('unknownDisposition', { disposition })}
          </p>
        ) : null}

        {/* Prazdny seznam: jedno klepnuti vyrobi vsechny mistnosti dispozice. */}
        {rooms.length === 0 && knownDisposition && disposition !== null ? (
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--tinted"
            onClick={() => onRooms(roomsForDisposition(template, disposition))}
            data-testid="nab3-generate"
          >
            {trRooms('generate', { disposition })}
          </button>
        ) : null}

        {/*
          Zmenena dispozice NIC NEMAZE (zadani R.B.): nabidne se, co pribyde,
          a co je nad rozpad, se jen vypise — odebrani je vzdy akce cloveka.
        */}
        {rooms.length > 0 && knownDisposition && disposition !== null && !plan.matches ? (
          <div className="rh-fi-rooms__plan" data-testid="nab3-plan">
            <p className="rh-fi-note rh-fi-note--warn">{trRooms('planChanged', { disposition })}</p>
            {plan.add.length > 0 ? (
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--tinted"
                onClick={() => onRooms([...rooms, ...plan.add])}
                data-testid="nab3-plan-add"
              >
                {trRooms('planAdd', { names: plan.add.map((room) => String(room['name'] ?? '')).join(', ') })}
              </button>
            ) : null}
            {plan.extra.length > 0 ? (
              <p className="rh-fi-note" data-testid="nab3-plan-extra">
                {trRooms('planExtra', { names: plan.extra.map((room) => String(room['name'] ?? '')).join(', ') })}
              </p>
            ) : null}
          </div>
        ) : null}

        {rooms.length > 0 ? (
          <div className="rh-fi-cl__chips" data-testid="nab3-bulk">
            <span className="rh-fi-cl__where">{trRooms('bulkTitle')}</span>
            {template.bulkPresets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className="rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip"
                onClick={() => onRooms(applyBulkPreset(template, rooms, preset.id))}
                data-testid={`nab3-bulk-${preset.id}`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        ) : null}

        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          onClick={() => setAddOpen((open) => !open)}
          data-testid="nab3-add-toggle"
        >
          <Plus size={18} aria-hidden="true" />
          {trRooms('addRoom')}
        </button>
        {addOpen ? (
          <div className="rh-fi-cl__chips" data-testid="nab3-add-types">
            {template.roomTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                className="rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip"
                onClick={() => {
                  onRooms(addRoom(template, rooms, type.value));
                  setAddOpen(false);
                }}
                data-testid={`nab3-add-${type.value}`}
              >
                {type.label}
              </button>
            ))}
          </div>
        ) : null}
      </section>

      {rooms.map((room) => {
        const roomId = String(room['id'] ?? '');
        const roomType = mediaRoomTypeOf(template, room);
        const done = roomDone(template, room);
        const summary = roomSummary(template, room);
        return (
          <details key={roomId} className="rh-v4-group rh-fi-cl__group" data-testid={`nab3-room-${roomId}`}>
            <summary className="rh-v4-row rh-fi-cl__groupHead" data-testid={`nab3-room-head-${roomId}`}>
              <span className="rh-v4-row__text">
                <span className="rh-v4-row__title">{String(room['name'] ?? '')}</span>
                {/* Souhrn do smlouvy; dokud je v nem jen nazev, nema co ukazovat. */}
                <span className="rh-v4-row__sub" data-testid={`nab3-room-summary-${roomId}`}>
                  {summary === String(room['name'] ?? '') ? '' : summary}
                </span>
              </span>
              <span className="rh-v4-row__value" data-testid={`nab3-room-done-${roomId}`}>
                {trRooms('fieldsDone', { done, total: template.fields.length })}
              </span>
            </summary>

            {/*
              Krok NAB4 — MIKROFON U MISTNOSTI. Omezeni rozboru jsou pole
              sablony mistnosti; do navrhu jde cely seznam mistnosti, protoze
              jedna veta zmini vic mistnosti („v obýváku plovoucí, kuchyň
              dlažba") a navrh ma svitit u te spravne.
            */}
            {recorder !== undefined && onDictate !== undefined ? (
              <DictationMic
                recorder={recorder}
                state={dictations[roomScopeKey(roomId)]}
                testid={`nab4-room-${roomId}`}
                onRecorded={(blob, durationS) => onDictate(roomScopeKey(roomId), blob, durationS)}
              />
            ) : null}

            <div className="rh-fi-rooms__tools">
              <span className="rh-fi-cl__where">{trRooms('copyPrevious')}</span>
              {(['floor', 'windows', 'shading'] as const).map((group) => (
                <button
                  key={group}
                  type="button"
                  className="rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip"
                  onClick={() => onRooms(copyFromPreviousRoom(template, rooms, roomId, group))}
                  data-testid={`nab3-copy-${roomId}-${group}`}
                >
                  {trRooms(group === 'floor' ? 'copyFloor' : group === 'windows' ? 'copyWindows' : 'copyShading')}
                </button>
              ))}
            </div>

            <div className="rh-fi-rooms__tools">
              <label className="rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip" data-testid={`nab3-photo-${roomId}`}>
                <Camera size={18} aria-hidden="true" />
                {trRooms('photo')}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  aria-label={trRooms('photo')}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file !== undefined) onPhoto({ roomId, roomType }, file);
                    event.target.value = '';
                  }}
                  data-testid={`nab3-photo-input-${roomId}`}
                />
              </label>
              <span className="rh-fi-cl__where" data-testid={`nab3-photo-count-${roomId}`}>
                {trRooms('photoCount', { count: photoCounts[roomId] ?? 0 })}
              </span>
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--plain"
                onClick={() => onRooms(removeRoom(rooms, roomId))}
                data-testid={`nab3-remove-${roomId}`}
              >
                <Trash2 size={18} aria-hidden="true" />
                {trRooms('remove')}
              </button>
            </div>

            <div className="rh-fi-cl__input">
              <input
                className="rh-input rh-fi-input rh-fi-input--grow"
                defaultValue={String(room['name'] ?? '')}
                aria-label={trRooms('rename')}
                onBlur={(event) => onRooms(renameRoom(rooms, roomId, event.target.value))}
                data-testid={`nab3-rename-${roomId}`}
              />
            </div>

            {groups.map((group) => (
              <div key={group.id} className="rh-fi-rooms__group" data-testid={`nab3-group-${roomId}-${group.id}`}>
                <div className="rh-fi-cl__where">{group.title}</div>
                {group.fields
                  /* Typ a nazev se resi v hlavicce karty (prejmenovani, odebrani). */
                  .filter((field) => field.id !== 'room_type' && field.id !== 'name')
                  .map((field) => (
                    <Field
                      key={field.id}
                      field={field}
                      roomId={roomId}
                      value={room[field.id]}
                      suggestion={suggestionForRoomField(dictations, roomId, field.id)}
                      onSet={(value) => onRooms(setRoomField(template, rooms, roomId, field.id, value))}
                      onAcceptSuggestion={(suggestion) => onAcceptSuggestion?.(suggestion)}
                      onDismissSuggestion={(suggestion) => onDismissSuggestion?.(suggestion)}
                    />
                  ))}
              </div>
            ))}
          </details>
        );
      })}
    </div>
  );
}
